import { Skeleton, Stack, Typography } from "@mui/material";
import type { OverridableStringUnion } from "@mui/types";
import type { TypographyVariant } from "@mui/material/styles/createTypography";
import type { TypographyPropsVariantOverrides } from "@mui/material/Typography";
import { memo } from "react";
import { useTranslation } from "react-i18next";
import { AppliedFilters } from "~/Search/components/Filters/components/AppliedFilters/AppliedFilters";
import { SearchFilters } from "~/Search/components/Filters/SearchFilters";
import { selectVisibleFilters } from "~/Search/redux/filterConfigSelectors";
import { useSelector } from "react-redux";
import { selectProductResultAmount } from "~/Search/redux/searchSelectors";
import { isComingFromSeriesTreeClick } from "~/Search/redux/seriesTreeSelectors";
import { Filter } from "~/Search/filterConfigModels";
import { SearchDisplayMode } from "~/Search/models";
import { SearchPageType, useSearchPageType } from "~/Search/useSearchPageType";
import type { RootState } from "~/redux/store";
import { useSelectAppliedFilters } from "~/Search/components/Filters/components/AppliedFilters/useSelectAppliedFilters";
import { FilterSectionSkeleton } from "./components/FilterSectionSkeleton";
import { selectSearchDisplayMode } from "~/feature/UserPreferences/userPreferencesSelectors";

interface Props {
  titleFontVariant?: OverridableStringUnion<TypographyVariant | "inherit", TypographyPropsVariantOverrides>;
  hideTechFilterButtons?: boolean;
  loading?: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  methods?: any;
  onSubmit?: (data: Record<string, { min?: number | undefined; max?: number | undefined }>) => void;
}

export const ReduxFilterSection = memo(function ReduxFilterSection({
  titleFontVariant = "headlineSmall",
  hideTechFilterButtons,
  loading,
  methods,
  onSubmit,
}: Props) {
  const { t } = useTranslation();
  const isTabularDisplay = useSelector(selectSearchDisplayMode) === SearchDisplayMode.TABULAR;
  const isProductFamilySelected = useSelector(selectIsProductFamilySelected);
  const isPlp = useSearchPageType() === SearchPageType.PLP;

  const hideNonCategoryFilters = isTabularDisplay && !isProductFamilySelected && !isPlp;

  let filterConfig = useSelector(selectVisibleFilters);

  const productAmount = useSelector(selectProductResultAmount);
  const isComingFromSeriesTree = useSelector(isComingFromSeriesTreeClick);

  const appliedFilters = useSelectAppliedFilters();

  if (!loading && isComingFromSeriesTree) {
    filterConfig = filterConfig.filter((item) => item.type !== Filter.TAXONOMY);
  }

  if (!loading && !productAmount && appliedFilters.length === 0 && filterConfig.length === 0) {
    return null;
  }

  const title = (
    <Typography
      variant={titleFontVariant}
      sx={{
        fontWeight: "bold",
      }}
    >
      {loading ? <Skeleton /> : t("page.search.filters.title")}
    </Typography>
  );

  return (
    <Stack spacing={{ phone: 1, "desktop-s": 2 }}>
      {title}
      {loading && <FilterSectionSkeleton amount={5} />}
      {!loading && (
        <>
          <AppliedFilters appliedFilters={appliedFilters} hideClearAll={hideTechFilterButtons} />
          <SearchFilters
            filterConfig={filterConfig}
            hideTechFilterButtons={hideTechFilterButtons}
            hideNonCategoryFilters={hideNonCategoryFilters}
            methods={methods}
            onSubmit={onSubmit}
          />
        </>
      )}
    </Stack>
  );
});

const selectIsProductFamilySelected = (state: RootState) => state.srp.facets.selectedProductGroups.length > 0;
